import type { MemberPlan } from './billing.js';
import type { Role } from './roles.js';
import type { ScalePreset } from './scale.js';

/** Wire types shared by the API and the web client. */

export interface User {
  userId: string;
  email: string;
  name: string;
  createdAt: string;
  /** Absent on accounts created before the terms existed. */
  termsVersion?: string;
  /** When `termsVersion` was first accepted. ISO 8601. */
  termsAcceptedAt?: string;
}

export interface Workspace {
  workspaceId: string;
  name: string;
  createdAt: string;
}

/** A workspace you belong to and your role in it — what the repo can answer alone. */
export interface WorkspaceMembership extends Workspace {
  role: Role;
}

/**
 * ...and what plan it is on, which costs a billing read. What `GET /me` returns.
 *
 * `plan` is optional because a failed billing read is a real state, and it is
 * not `free`: reporting a comped workspace as Free is the wrong screen
 * `getBillingRecords` throws to avoid. Absent means unknown, and renders as
 * nothing — see `labelPlans` in `services/billing.ts`.
 */
export interface WorkspaceMembershipSummary extends WorkspaceMembership {
  plan?: MemberPlan;
}

export interface Member {
  userId: string;
  email: string;
  name: string;
  role: Role;
  joinedAt: string;
}

export type InvitationStatus = 'pending' | 'accepted' | 'revoked';

export interface Invitation {
  invitationId: string;
  workspaceId: string;
  email: string;
  role: Role;
  status: InvitationStatus;
  createdAt: string;
  expiresAt: string;
}

/** What an invitee sees before accepting — deliberately minimal. */
export interface InvitationPreview {
  workspaceName: string;
  email: string;
  role: Role;
  expiresAt: string;
}

/**
 * A grouping for a workspace's polls (#76).
 *
 * A folder holds no list of its own: the poll carries the `folderId` and the
 * dashboard groups over the list it already fetches. That is what keeps
 * renaming or deleting a folder a single-row write rather than a sweep across
 * every poll filed in it.
 */
export interface Folder {
  folderId: string;
  workspaceId: string;
  name: string;
  createdAt: string;
}

export type QuizStatus = 'draft' | 'live';

/**
 * A reply shown to the taker who picked this option, after their vote lands.
 *
 * Every field is optional and independent: a message alone is a note, a link
 * alone is a button. All three empty is the same as no follow-up, and is
 * normalized to absent on save.
 */
export interface FollowUp {
  /** Plain text. Never rendered as HTML or markdown. */
  message?: string;
  /** Absolute https URL. */
  url?: string;
  /** Text on the link button. Defaults to DEFAULT_FOLLOWUP_LABEL. */
  linkLabel?: string;
}

/**
 * A follow-up on its way to the one device that picked its option.
 *
 * Carries the optionId because a multi-select question can earn several at
 * once, and the phone prints each beside the choice that produced it — which it
 * cannot do from an unlabelled list.
 */
export interface OptionFollowUp {
  optionId: string;
  followUp: FollowUp;
}

export interface Option {
  optionId: string;
  text: string;
  /**
   * Withheld from every public projection — see publicOption in
   * packages/api/src/services/presentation.ts. It reaches a phone only in that
   * device's own vote response.
   */
  followUp?: FollowUp | null;
  /**
   * A picture of the thing this option names (#75), served from our own media
   * prefix. Unlike `followUp` above, it is *published* — a ballot nobody can
   * see the pictures on is not the feature.
   *
   * `text` stays required beside it, so an image is never an option's only
   * label and the bar's aria-label still has something to say.
   */
  imageUrl?: string;
}

/**
 * What kind of answer a question takes (#50).
 *
 * A discriminator rather than a set of independent flags, because the kinds are
 * mutually exclusive and each one decides which of the rules below apply. A
 * scale is still an ordered *option* set, so the vote path does not branch on
 * this at all — only authoring, validation and rendering do.
 */
export type QuestionType = 'choice' | 'scale';

export interface Question {
  questionId: string;
  text: string;
  options: Option[];
  /**
   * Absent means 'choice', which is every question saved before this field
   * existed, so no migration is needed. Written only for a kind that is not
   * the default, exactly as `multiSelect` is written only when true.
   */
  type?: QuestionType;
  /**
   * The scale a rating question is drawn on, and the thing its options are
   * generated from. Present only when `type` is 'scale'.
   *
   * The preset rather than a min and a max: two numbers could disagree with
   * each other and with the option list they describe, and there would be no
   * one place to add an anchor label to.
   */
  scale?: ScalePreset;
  /**
   * Whether one device may pick several options. Absent on questions saved
   * before the field existed, and read as false everywhere.
   *
   * Never set on a scale question — `validateScreens` rejects the combination,
   * because summing option counts is the denominator a mean is taken over.
   *
   * A boolean rather than a cap, because "at most N" is a separate rule an
   * author has not asked for: adding `maxSelections` later is a new optional
   * field, whereas encoding the mode *as* a cap would make 1 and undefined mean
   * the same thing and leave "as many as you like" chasing the option count.
   */
  multiSelect?: boolean;
  /**
   * A picture the question is asked about (#75) — the thing that is quicker to
   * show than to describe.
   *
   * Reaches a taker's phone through `publicScreen`, which spreads `Question`
   * wholesale, and the projector through `buildQuestionResults`, which does
   * not: `BigScreenBody` renders question content only through `ResultBars`,
   * off `state.results`. Both paths carry it, which is why it also appears on
   * `QuestionResult` below.
   */
  imageUrl?: string;
}

export interface Screen {
  screenId: string;
  questions: Question[];
  /**
   * How long the room gets on this screen, in whole seconds (#77). Absent means
   * no clock, which is every poll that exists today, so this needs no migration.
   *
   * The unit is the screen rather than the question because the presenter
   * advances by `currentScreenIndex` and a screen holds up to
   * MAX_QUESTIONS_PER_SCREEN questions — timing each of three separately would
   * put three independent clocks on one projector.
   */
  timeLimitSeconds?: number;
}

/** How many questions may sit side by side on the big screen. */
export type QuestionColumns = 1 | 2 | 3;

export interface Theme {
  primaryColor: string;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
  backgroundImageUrl?: string | null;
  logoUrl?: string | null;
  /**
   * Maximum questions across the big screen. Absent on polls saved before the
   * field existed, and read as 1 everywhere — see the web client's
   * questionGrid.
   *
   * A count rather than a "1x3" string: rows-by-columns and columns-by-rows
   * are both idiomatic, and that ambiguity would be permanent once it is in a
   * wire type.
   */
  questionColumns?: QuestionColumns;
}

export interface Quiz {
  quizId: string;
  workspaceId: string;
  title: string;
  status: QuizStatus;
  joinCode: string;
  theme: Theme;
  screens: Screen[];
  /** Index into `screens`; meaningful only while status is 'live'. */
  currentScreenIndex: number;
  createdAt: string;
  updatedAt: string;
  /**
   * The folder this poll is filed in (#76). Absent means unfiled, which is
   * every poll saved before folders existed — so this needs no migration.
   *
   * May name a folder that has since been deleted. `folderId` is not indexed,
   * so unfiling on delete would mean rewriting every poll in it; the dangling
   * id is left standing and a reader that does not recognise one renders the
   * poll as unfiled.
   */
  folderId?: string;
  /**
   * One of the example polls a new account is given at signup, which show off
   * what a poll can look like (`packages/api/src/lib/samples.ts`). Stored only
   * when true, like `multiSelect`.
   *
   * A sample is written without the usage index's keys, so every metric and
   * every lifecycle-email condition reads a workspace holding only samples as
   * holding no poll — the account has not made anything yet. That stays true
   * however much the sample is edited, because no later write adds those keys;
   * a duplicate is an ordinary poll and does not carry this flag. Console-only:
   * no public view is built from this field.
   */
  sample?: true;
  /** The open presentation run, when one is live. */
  currentSessionId?: string | null;
  /**
   * Whether the workspace was on a paid plan when the run currently on screen
   * started.
   *
   * Stamped once, by `POST /quizzes/{quizId}/presentation`, from the single
   * billing read that also decides the run's ceiling — so the taker's
   * unauthenticated path never reads billing, and a plan that lapses or is
   * bought mid-show cannot move anything under a room that is already voting.
   * The ceiling itself lives on the session record beside the meter it is
   * compared against; this is the *entitlement*, not the number.
   *
   * **Three states, not two.** Absent means the plan could not be read — a
   * billing read that timed out, or a run that started before this field
   * existed. It is deliberately not a synonym for `false`, because the two
   * readers of this field want opposite defaults when the answer is unknown:
   *
   * | Reader                            | Tests        | Absent means      |
   * | --------------------------------- | ------------ | ----------------- |
   * | The audience cap (`public/routes`) | `=== false` | do not cap        |
   * | Vendor branding (#91)              | `=== true`  | show the branding |
   *
   * Each names the value it wants, so neither inherits the other's default. A
   * boolean with a default would have to pick one and be wrong for the other:
   * a failed read must never shut a room out of a poll, and must never strip a
   * free workspace's branding either.
   *
   * Never handed to a public view as it stands. What a public surface is told
   * is the derived `PublicQuizState.vendorMark` — one boolean, already
   * resolved against the switch-on date — so the tri-state stays on the server
   * side of the wire where both its readers live.
   */
  entitledRun?: boolean;
  /**
   * Whether the big screen is currently showing the distribution (#49).
   *
   * Absent on polls saved before the field existed and read as `true`, which is
   * the behaviour every poll has today — so this needs no migration. Reset by
   * every presentation state change, so a hold never outlives the screen it was
   * called on.
   */
  resultsVisible?: boolean;
  /**
   * Whether the screen being presented still accepts votes (#49).
   *
   * Per current screen, not per question: it is cleared alongside
   * `currentScreenIndex` on every advance. #77's countdown reuses this same
   * closed state when it expires rather than inventing a second way for a
   * question to stop accepting votes — see `votingHasClosed`, which is the one
   * predicate every client renders off.
   */
  votingClosed?: boolean;
  /**
   * Whether the floor may ask questions right now (#118).
   *
   * Absent means closed, which is every poll that exists today — so Q&A ships
   * dark rather than putting an ask box on every taker's phone the moment this
   * deploys, and a presenter opts a room into it.
   *
   * Set by `POST /quizzes/{quizId}/presentation/controls` beside the other two
   * presenter flags, and deliberately **not** reset by `setPresentationState`
   * the way those two are: results and voting are per screen, but a room that
   * has been invited to ask questions stays invited across an advance.
   */
  qandaOpen?: boolean;
  /**
   * The asked question currently on the projector (#118), or null when none
   * is.
   *
   * Lives on the quiz rather than on the question item so that "which one is
   * up" is a single read on the item every public surface already fetches —
   * the same reason `currentScreenIndex` is here rather than on a screen.
   */
  presentedAskedQuestionId?: string | null;
  /**
   * When the current screen's clock runs out (#77). ISO 8601, absolute.
   *
   * Written by `setPresentationState` in the same update that moves
   * `currentScreenIndex`, from the screen's own `timeLimitSeconds`. Null on a
   * screen with no limit, and on a poll that is not being presented.
   *
   * Absolute rather than a remaining-seconds count: nothing fires at zero, so
   * every reader compares this against its own clock, and a duration would be
   * stale by the time it landed.
   */
  screenDeadlineAt?: string | null;
}

/** Dashboard row — omits the full structure to keep list responses small. */
export interface QuizSummary {
  quizId: string;
  workspaceId: string;
  title: string;
  status: QuizStatus;
  joinCode: string;
  screenCount: number;
  questionCount: number;
  totalVotes: number;
  updatedAt: string;
  /** #76 — what the dashboard groups on. Absent means unfiled. */
  folderId?: string;
  /** One of the signup examples; see `Quiz.sample`. Absent on every other poll. */
  sample?: true;
}

export interface OptionResult {
  optionId: string;
  text: string;
  /** How many times this option was picked. */
  count: number;
  /** Share of `QuestionResult.totalVotes`, so the denominator is always people. */
  percentage: number;
  /** The option's picture (#75), carried through from `Option`. */
  imageUrl?: string;
}

export interface QuestionResult {
  questionId: string;
  text: string;
  /**
   * How many devices answered — not how many options were picked. The two are
   * the same number for a single-choice question, and differ for a multi-select
   * one, where one device can contribute several option counts.
   */
  totalVotes: number;
  /**
   * Set only when the question accepts several answers, which is when the
   * percentages below can sum past 100. A view that prints them has to say so
   * or the numbers read as a bug.
   */
  multiSelect?: boolean;
  /**
   * The question's kind and, for a scale, its preset — carried through from
   * `Question` (#50). Absent on a choice question, as it is on the question.
   *
   * Here rather than left to each view to fetch, because this projection is the
   * single one behind the results view, the big-screen broadcast and the sealed
   * run snapshot, and a mean cannot be taken without knowing which point each
   * option sits on. The snapshot is the case that settles it: a run describes
   * an evening that happened, so the scale it was rated on has to be frozen
   * with it rather than read off the poll as it stands today.
   */
  type?: QuestionType;
  scale?: ScalePreset;
  /**
   * The question's picture (#75), carried through from `Question` for the same
   * reason the scale above is: this projection is the only thing the big screen
   * renders question content from, so an image that stopped at the ballot would
   * reach every phone and never reach the projector.
   */
  imageUrl?: string;
  options: OptionResult[];
}

/**
 * How many answered, without saying what they answered (#49).
 *
 * What a held big screen may know. It carries no `options` at all rather than
 * options with zeroed counts: a zero is indistinguishable from a real zero, and
 * a view that renders one would paint a chart that looks like a result.
 */
export interface QuestionTally {
  questionId: string;
  text: string;
  /** Devices that have answered — the same denominator as `QuestionResult`. */
  totalVotes: number;
}

export interface ScreenResult {
  screenId: string;
  questions: QuestionResult[];
}

export interface QuizResults {
  quizId: string;
  title: string;
  totalVotes: number;
  screens: ScreenResult[];
}

/** One sealed presentation run, as the history list shows it (#46). */
export interface SessionSummary {
  sessionId: string;
  quizId: string;
  startedAt: string;
  endedAt: string;
  voteCount: number;
  participantCount: number;
  screensAdvanced: number;
}

/**
 * One run's results, frozen when the presentation ended (#46).
 *
 * `screens` deliberately does *not* follow the current-structure rule that
 * governs `QuizResults` (§5). Accumulated results describe the poll as it
 * stands, so a deleted question should vanish from them; a historical run
 * describes an evening that happened, so its questions and their wording are
 * whatever was on the screen that night — even after the poll is edited, and
 * even after it is deleted.
 */
export interface SessionResults {
  sessionId: string;
  quizId: string;
  /** The poll's title on the night, not its title now. */
  title: string;
  startedAt: string;
  endedAt: string;
  totalVotes: number;
  participantCount: number;
  screens: ScreenResult[];
}

/**
 * Public payload for the big screen and taker views.
 *
 * `quizId` is omitted for takers on purpose: it is the only credential the
 * big-screen results endpoint requires, so handing it to a phone would let the
 * audience read the live distribution the taker view is meant to withhold.
 * Takers address the quiz by join code instead.
 */
export interface PublicQuizState {
  quizId?: string;
  title: string;
  status: QuizStatus;
  theme: Theme;
  /**
   * Whether this run's public surfaces carry the FloorPoll mark (#91).
   *
   * Derived server-side from the entitlement stamped on the quiz item at
   * presentation start, and never from anything a client says. A phone
   * declares its own `view` on connect, so a client-supplied branding flag
   * would be a paywall any taker could switch off from the URL bar — the same
   * reasoning that withholds `followUp` from *every* public view rather than
   * gating it on `viewSeesResults`.
   *
   * Checked against `viewSeesResults` per CLAUDE.md and deliberately **not**
   * gated on it: taker, screen and presenter are told the same thing. A mark
   * is not a result, and the two surfaces that render it are exactly the ones
   * a phone and a projector see.
   *
   * `true` means "render it", so the two unknowns fall the two right ways. A
   * payload from a server too old to send this field reads as no mark, which
   * is the safe direction for a workspace that has paid; a plan that could not
   * be read reads as mark shown, which is the safe direction for the paywall.
   * See `showsVendorMarkAt` for the second half of that pair.
   *
   * Not a `Theme` field. `PATCH /quizzes/{quizId}` writes the theme wholesale
   * from the request body, so an entitlement living there would be one request
   * away from being switched off by anyone holding `quiz:write`.
   */
  vendorMark: boolean;
  currentScreenIndex: number;
  screenCount: number;
  screen: Screen | null;
  /** Present only for the big-screen view. */
  results?: QuestionResult[];
  /**
   * Present instead of `results` for a results-bearing view whose numbers are
   * being held back (#49). A taker gets neither: participation is still a fact
   * about the vote, and `viewSeesResults` withholds it from a phone exactly as
   * it withholds the distribution.
   */
  tallies?: QuestionTally[];
  /**
   * Whether the distribution is being shown right now (#49).
   *
   * Sent to **every** view including `taker`, and deliberately not gated on
   * `viewSeesResults`: it is the presenter's intent, not a number, and a phone
   * renders no results either way. The big screen reads it to tell "held" from
   * "nobody has voted yet" rather than inferring it from a missing field.
   */
  resultsVisible: boolean;
  /**
   * Whether the current screen still accepts votes (#49). Goes to every view
   * for the same reason — the taker is the view that most needs it, because its
   * buttons lock on it.
   */
  votingClosed: boolean;
  /**
   * When this screen's clock runs out, or null when it has none (#77).
   *
   * Checked against `viewSeesResults` per CLAUDE.md and deliberately sent to
   * **every** view including `taker`: a clock is not a result. The projector is
   * the surface it is for, and the phone is the surface that locks on it.
   *
   * Null rather than absent so a reducer merging a fixed field list clears a
   * deadline left behind by the previous screen.
   */
  screenDeadlineAt: string | null;
  /**
   * Whether the floor may ask questions right now (#118).
   *
   * Checked against `viewSeesResults` per CLAUDE.md and deliberately sent to
   * **every** view: an invitation to speak is not a result. The taker is the
   * surface it is for — its ask box renders off this — and the projector reads
   * it to decide whether to show the join prompt.
   */
  qandaOpen: boolean;
  /**
   * The asked question on the projector right now, or null (#118).
   *
   * Null rather than absent for the same reason `screenDeadlineAt` is: a
   * client merging a fixed field list has to be able to clear one.
   */
  presentedAskedQuestionId: string | null;
  /**
   * The server's own time when this payload was built (#77).
   *
   * The deadline above is useless on a device whose clock is wrong, and phone
   * clocks are wrong often enough to matter in a room. Sending both lets a
   * client measure its drift once and count down from a corrected now, rather
   * than trusting its own.
   */
  serverTime: string;
}

export const MIN_OPTIONS_PER_QUESTION = 2;
export const MAX_OPTIONS_PER_QUESTION = 10;
export const MAX_QUESTIONS_PER_SCREEN = 3;
